// Shopping domain layer.
//
// Two entity types — both sub-concepts of the shopping list — stored in the
// shared partition (PK = "shared"):
//
//   Catalogue item   SK = "shopping-list.catalogue#<uuid>"
//     Every item ever added to the list. Persists across list clears and
//     powers name/section autocomplete. Fields: name, section.
//
//   List item        SK = "shopping-list.item#<uuid>"
//     An entry currently on the shopping list. References a catalogue item
//     by its full itemId via `catalogueRef`. Fields: catalogueRef, amount,
//     special, notes. The `checked` state is local-only (page state) and
//     never stored.
//
//     A list item MAY also be "ingredient-backed": it carries an optional
//     `ingredientRef` (the full `ingredient#…` itemId) plus a `buyUnit`
//     snapshot (the pack noun at add time). An ingredient-backed item can be
//     "marked as bought" — that turns one pack into real on-hand ingredient
//     stock (see markBought). An item WITHOUT an `ingredientRef` is "arbitrary"
//     (a free-text entry) and buying it does nothing special — it is simply
//     removed from the list, exactly as before.
//
// A list item carries no name/section of its own — those are looked up
// from the referenced catalogue item at render time, so a catalogue
// section change (req 5) is reflected immediately on the list.

import { addItem, listItems, updateItem, deleteItem, SHARED_PARTITION_KEY } from './db.js';
import { addStockEntry, formatLocalDate } from './stock.js';
import { buyServingsOf, isAlwaysAvailable } from './ingredients.js';

const CATALOGUE_PREFIX = 'shopping-list.catalogue#';
const LIST_ITEM_PREFIX = 'shopping-list.item#';

/** Section applied to a brand-new catalogue item when none is chosen. */
export const DEFAULT_SECTION = 'Other';

/** A catalogue item: something that has been on the list at some point. */
export class ShoppingItem {
  constructor(raw) {
    /** Full DynamoDB sort key, e.g. "shopping-list.catalogue#<uuid>". */
    this.itemId = raw.itemId;
    this.name = raw.name ?? 'Unnamed item';
    this.section = raw.section ?? DEFAULT_SECTION;
  }
}

/** An entry currently on the shopping list, referencing a catalogue item. */
export class ShoppingListItem {
  constructor(raw) {
    /** Full DynamoDB sort key, e.g. "shopping-list.item#<uuid>". */
    this.itemId = raw.itemId;
    /** The referenced catalogue item's full itemId. */
    this.catalogueRef = raw.catalogueRef;
    /** Free-text amount, e.g. "2 boxes" — used for ARBITRARY (non-linked) items. */
    this.amount = raw.amount ?? '';
    this.special = raw.special ?? false;
    this.notes = raw.notes ?? '';
    /**
     * Optional link to a shared ingredient (`ingredient#…` itemId). When set,
     * the item is "ingredient-backed" and can be marked-as-bought into stock;
     * when absent it is an "arbitrary" free-text item (buying is a no-op).
     */
    this.ingredientRef = raw.ingredientRef ?? null;
    /** Snapshot of the pack noun (buyUnit) at add time, for display. */
    this.buyUnit = raw.buyUnit ?? null;
    /**
     * For an ingredient-backed item: the quantity (in SERVINGS) to add to stock
     * when marked bought. Null falls back to one pack (buyServingsOf) at buy
     * time. Arbitrary items use the free-text `amount` instead and ignore this.
     */
    this.amountServings = raw.amountServings ?? null;
    /**
     * True when this row was pushed onto the list by a menu save. Only such rows
     * are reconciled by `reconcileMenuShoppingList` on later saves; manually-added
     * rows never carry this flag and are left untouched.
     */
    this.menuManaged = raw.menuManaged ?? false;
  }
}

/** Whether a list item is linked to a known ingredient (vs an arbitrary entry). */
export function isIngredientBacked(listItem) {
  return Boolean(listItem?.ingredientRef);
}

/* ------------------------------------------------------------------ *
 * Catalogue
 * ------------------------------------------------------------------ */

export async function listCatalogue(doc) {
  return listItems(doc, SHARED_PARTITION_KEY, CATALOGUE_PREFIX, (raw) => new ShoppingItem(raw));
}

/** Case-insensitive lookup of a catalogue item by name. Returns null if none. */
export function findCatalogueByName(catalogue, name) {
  const needle = name.trim().toLowerCase();
  return catalogue.find((c) => c.name.trim().toLowerCase() === needle) ?? null;
}

export async function addCatalogueItem(doc, { name, section }) {
  const raw = await addItem(doc, SHARED_PARTITION_KEY, CATALOGUE_PREFIX, {
    name: name.trim(),
    section: section.trim(),
  });
  return new ShoppingItem(raw);
}

/** Req 5: update an existing catalogue item's section. */
export async function updateCatalogueSection(doc, itemId, section) {
  await updateItem(doc, SHARED_PARTITION_KEY, itemId, {
    section: section.trim(),
  });
}

/* ------------------------------------------------------------------ *
 * List items
 * ------------------------------------------------------------------ */

export async function listListItems(doc) {
  return listItems(doc, SHARED_PARTITION_KEY, LIST_ITEM_PREFIX, (raw) => new ShoppingListItem(raw));
}

export async function addListItem(
  doc,
  { catalogueRef, amount, special, notes, ingredientRef, buyUnit, amountServings, menuManaged },
) {
  const fields = {
    catalogueRef,
    amount: amount ?? '',
    special: special ?? false,
    notes: notes ?? '',
  };
  // Only stamp the ingredient link when present, so arbitrary items stay clean.
  if (ingredientRef) {
    fields.ingredientRef = ingredientRef;
    if (buyUnit) fields.buyUnit = buyUnit;
    if (amountServings !== null && amountServings !== undefined) {
      fields.amountServings = amountServings;
    }
  }
  // Only stamp the menu-managed flag when true, so manual items stay clean.
  if (menuManaged) fields.menuManaged = true;
  const raw = await addItem(doc, SHARED_PARTITION_KEY, LIST_ITEM_PREFIX, fields);
  return new ShoppingListItem(raw);
}

export async function removeListItem(doc, listItemId) {
  await deleteItem(doc, SHARED_PARTITION_KEY, listItemId);
}

/** Update the stored buy quantity (in servings) of an existing list item. */
export async function updateListItemServings(doc, listItemId, amountServings) {
  await updateItem(doc, SHARED_PARTITION_KEY, listItemId, { amountServings });
}

/**
 * Update per-list-item fields on an existing row (from the edit dialog). Only the
 * keys present in `fields` are written — pass `{ special, notes, amount }` (and
 * `amountServings` for arbitrary items if ever needed). The item's SECTION is NOT
 * here: section lives on the shared catalogue item (see updateCatalogueSection),
 * so editing it persists across every future add of the same item.
 */
export async function updateListItemFields(doc, listItemId, fields) {
  const patch = {};
  for (const key of ['special', 'notes', 'amount', 'amountServings']) {
    if (Object.prototype.hasOwnProperty.call(fields, key)) patch[key] = fields[key];
  }
  if (Object.keys(patch).length === 0) return;
  await updateItem(doc, SHARED_PARTITION_KEY, listItemId, patch);
}

/**
 * Remove an item from the list WITHOUT buying it (semantic alias of
 * removeListItem — both ingredient-backed and arbitrary items are just dropped,
 * with no stock side effect). Accepts a list item or a bare itemId.
 */
export async function removeFromList(doc, listItem) {
  const id = typeof listItem === 'string' ? listItem : listItem?.itemId;
  await removeListItem(doc, id);
}

/**
 * Mark a list item as BOUGHT.
 *
 * For an ingredient-backed item, this turns one purchasable pack into real
 * on-hand ingredient stock — a new `stock#` row of `buyServingsOf(ingredient)`
 * servings, obtained today (local date) — then removes the list item.
 *
 * No stock is written (the item is simply removed, like removeFromList) when:
 *   • the item is arbitrary (no ingredientRef) or its ingredient can't resolve;
 *   • the ingredient is ALWAYS AVAILABLE — a pantry staple or a restaurant item
 *     (always on hand — it doesn't track as a stock quantity).
 *
 * `userId` is the caller's private partition (stock is per-user; the shopping
 * list is shared). `ingredient` is the resolved ingredient record (or null).
 * Returns { stocked, stockRef } — stocked=false when no stock was written.
 */
export async function markBought(doc, userId, listItem, ingredient, { now = new Date() } = {}) {
  const backed = isIngredientBacked(listItem) && ingredient;
  if (!backed || isAlwaysAvailable(ingredient)) {
    await removeFromList(doc, listItem);
    return { stocked: false, stockRef: null };
  }

  // Use the item's chosen quantity (in servings). Fall back to one pack's worth
  // when unset (older items, or a quick add that skipped the quantity control).
  const servings =
    listItem.amountServings !== null && listItem.amountServings !== undefined
      ? listItem.amountServings
      : buyServingsOf(ingredient);
  const stored = await addStockEntry(doc, userId, {
    ingredientRef: listItem.ingredientRef,
    servings,
    obtainedDate: formatLocalDate(now),
  });
  await removeFromList(doc, listItem);
  return { stocked: true, stockRef: stored.itemId };
}

/* ------------------------------------------------------------------ *
 * Orchestration
 * ------------------------------------------------------------------ */

/**
 * Add an item to the shopping list, reconciling the catalogue first.
 *
 *   1. Look up the catalogue by name (case-insensitive).
 *   2. If found and the section differs, update the catalogue section (req 5).
 *   3. If not found, create a new catalogue item.
 *   4. Create a list item referencing the catalogue item.
 *
 * `catalogue` is the current catalogue array (so we avoid an extra fetch);
 * pass the result of listCatalogue(doc).
 *
 * Returns { catalogueItem, listItem }.
 */
export async function addToList(
  doc,
  catalogue,
  { name, section, amount, special, notes, ingredientRef, buyUnit, amountServings, menuManaged },
) {
  let catalogueItem = findCatalogueByName(catalogue, name);

  if (catalogueItem) {
    // Only overwrite an EXISTING catalogue item's section with a real, non-default
    // value the caller explicitly chose. A blank or default ('Other') section —
    // e.g. a menu-driven re-add — must NOT clobber a section the user set earlier,
    // since section is the persistent, shared attribute of the catalogue item.
    const chosen = section.trim();
    if (chosen && chosen !== DEFAULT_SECTION && chosen !== catalogueItem.section) {
      await updateCatalogueSection(doc, catalogueItem.itemId, chosen);
      catalogueItem.section = chosen;
    }
  } else {
    catalogueItem = await addCatalogueItem(doc, {
      name,
      section: section.trim() || DEFAULT_SECTION,
    });
  }

  const listItem = await addListItem(doc, {
    catalogueRef: catalogueItem.itemId,
    amount,
    special,
    notes,
    ingredientRef,
    buyUnit,
    amountServings,
    menuManaged,
  });

  return { catalogueItem, listItem };
}

/**
 * Total servings a `wanted` buy represents (packs × servings-per-pack). Falls
 * back to the pack count when buyServings is unknown, so mark-bought still adds
 * something sensible.
 */
function wantedServings(w) {
  const packs = Number(w.packs) || 0;
  const per = Number(w.buyServings) || 0;
  return per > 0 ? packs * per : packs;
}

/**
 * PURE preview of what `reconcileMenuShoppingList(…, wanted)` would change,
 * given the current list items (`listListItems` rows). Same rules: only
 * `menuManaged` ingredient-backed rows are compared.
 * @returns {{ added: object[], updated: object[], removed: object[] }} —
 *   added: the `wanted` items (+ `servings`) not yet on the list;
 *   updated: `{ ...wanted, servings, fromServings }` whose amount changes;
 *   removed: the list items that would be dropped.
 */
export function previewMenuShoppingChanges(listItems, wanted) {
  const existing = (listItems ?? []).filter((li) => li.menuManaged && li.ingredientRef);
  const existingByRef = new Map(existing.map((li) => [li.ingredientRef, li]));
  const wantedByRef = new Map((wanted ?? []).map((w) => [w.ingredientRef, w]));
  const added = [];
  const updated = [];
  for (const [ref, w] of wantedByRef) {
    const servings = wantedServings(w);
    if (servings <= 0) continue;
    const current = existingByRef.get(ref);
    if (!current) added.push({ ...w, servings });
    else {
      const fromServings = Number(current.amountServings) || 0;
      if (fromServings !== servings) updated.push({ ...w, servings, fromServings });
    }
  }
  const removed = existing.filter((li) => !wantedByRef.has(li.ingredientRef));
  return { added, updated, removed };
}

/**
 * Reconcile the MENU-MANAGED shopping-list rows to match `wanted` — the net buy
 * list a menu save produced (from `buysFromMenus`). Each `wanted` item is
 * `{ ingredientRef, name, packs, buyServings, buyUnit }`.
 *
 * Only rows flagged `menuManaged` are touched; manual + arbitrary rows are left
 * completely alone (so re-saves never duplicate or clobber the user's own items).
 * Keyed by `ingredientRef`:
 *   • wanted & not on list  → add   (ingredient-backed, menuManaged)
 *   • wanted & qty changed  → update the stored servings
 *   • on list & not wanted  → remove (its meal was removed / now sourced from stock)
 *
 * Idempotent: calling twice with the same `wanted` makes no further changes.
 * Returns a small summary for logging/tests: { added, updated, removed }.
 */
export async function reconcileMenuShoppingList(doc, catalogue, wanted) {
  const listItems = await listListItems(doc);
  const changes = previewMenuShoppingChanges(listItems, wanted);
  const existingByRef = new Map(
    listItems
      .filter((li) => li.menuManaged && li.ingredientRef)
      .map((li) => [li.ingredientRef, li]),
  );
  let cat = catalogue;

  for (const w of changes.added) {
    const { catalogueItem } = await addToList(doc, cat, {
      name: w.name ?? w.ingredientRef,
      section: 'Other',
      ingredientRef: w.ingredientRef,
      buyUnit: w.buyUnit ?? null,
      amountServings: w.servings,
      menuManaged: true,
    });
    // Keep the local catalogue snapshot fresh so a second new item in the same
    // run reuses the just-created catalogue row instead of duplicating it.
    if (catalogueItem && !cat.some((c) => c.itemId === catalogueItem.itemId)) {
      cat = [...cat, catalogueItem];
    }
  }
  for (const w of changes.updated) {
    await updateListItemServings(doc, existingByRef.get(w.ingredientRef).itemId, w.servings);
  }
  for (const li of changes.removed) await removeListItem(doc, li.itemId);

  return {
    added: changes.added.length,
    updated: changes.updated.length,
    removed: changes.removed.length,
  };
}

/**
 * Fetch the current list, join each entry to its catalogue item, and group
 * by the catalogue item's current section.
 *
 * Returns an array of groups: [{ section, entries: [{ listItem, item }] }],
 * sorted by section name, entries sorted by item name. Orphaned list items
 * (whose catalogue item no longer exists) are grouped under "Unknown".
 */
export async function getGroupedList(doc) {
  const [catalogue, listItemEntries] = await Promise.all([listCatalogue(doc), listListItems(doc)]);

  const byId = new Map(catalogue.map((c) => [c.itemId, c]));
  const groups = new Map();

  for (const listItem of listItemEntries) {
    const item = byId.get(listItem.catalogueRef) ?? null;
    const section = item ? item.section : 'Unknown';
    if (!groups.has(section)) groups.set(section, []);
    groups.get(section).push({ listItem, item });
  }

  // Case-insensitive, locale-aware ordering for both sections and items.
  const cmp = (a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' });

  return [...groups.entries()]
    .map(([section, entries]) => ({
      section,
      entries: entries.sort((a, b) => cmp(a.item?.name ?? '', b.item?.name ?? '')),
    }))
    .sort((a, b) => cmp(a.section, b.section));
}
